import { ClickOutside } from 'vuetify/directives';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';

/**
 * Makes Vuetify's click-outside work for overlays teleported into the legacy iframe.
 *
 * `vuetify/lib/directives/click-outside` registers its `click`/`mousedown` capture listeners on the
 * module-global `document` — the host page, because the Vue bundle runs in the parent window. An
 * overlay mounted through `:attach="overlayTarget"` lives in the iframe document, whose events never
 * reach the host, so no v-menu (and no v-select menu) ever closed on an outside click.
 *
 * VOverlay imports the directive object directly rather than resolving it from the app registry, so
 * the fix is to wrap that shared object's hooks once, at bootstrap, and re-register the very handlers
 * the original `mounted` built on the other documents too. Reusing those closures matters: they carry
 * the `binding` (and therefore VOverlay's `closeConditional`, which keeps a `scrim: true` menu closing
 * only on its own scrim), so behaviour stays identical — only the listening surface widens.
 */
export function patchVuetifyClickOutside() {
	if (ClickOutside.__iframeAware) {
		return;
	}

	const originalMounted = ClickOutside.mounted;
	const originalBeforeUnmount = ClickOutside.beforeUnmount;

	// The overlay is teleported before the directive's post-render hooks run, so `ownerDocument` is
	// already the iframe's; the bridge lookup is the safety net for anything mounted the other way.
	function getExtraDocuments(el) {
		const documents = new Set();
		const iframeDocument = iframeDataBridge.getIframeWindow()?.document || null;

		[el?.ownerDocument, iframeDocument].forEach((candidate) => {
			if (candidate && candidate !== document) {
				documents.add(candidate);
			}
		});

		return [...documents];
	}

	function getHandlers(el, binding) {
		return el?._clickOutside?.[binding?.instance?.$?.uid] || null;
	}

	ClickOutside.mounted = function mounted(el, binding) {
		originalMounted(el, binding);

		const handlers = getHandlers(el, binding);
		if (!handlers) {
			return;
		}

		handlers.extraDocuments = getExtraDocuments(el);
		handlers.extraDocuments.forEach((extraDocument) => {
			extraDocument.addEventListener('click', handlers.onClick, true);
			extraDocument.addEventListener('mousedown', handlers.onMousedown, true);
		});
	};

	ClickOutside.beforeUnmount = function beforeUnmount(el, binding) {
		const handlers = getHandlers(el, binding);
		handlers?.extraDocuments?.forEach((extraDocument) => {
			extraDocument.removeEventListener('click', handlers.onClick, true);
			extraDocument.removeEventListener('mousedown', handlers.onMousedown, true);
		});

		originalBeforeUnmount(el, binding);
	};

	ClickOutside.__iframeAware = true;
}
